import { useEffect, useState } from "react";

import {
  fetchStorageBlob,
  needsAuthenticatedStorageFetch,
} from "@/lib/storageFetch";

export type StorageObjectUrlResult =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; url: string }
  | { status: "error" };

type ResolvedState =
  | { kind: "ready"; src: string; url: string }
  | { kind: "error"; src: string };

/**
 * Resolves authenticated storage proxy / object paths to a browser-usable URL.
 * Public and blob/data URLs are returned as-is with status `"ready"`.
 */
export function useStorageObjectUrl(
  src: string | undefined,
): StorageObjectUrlResult {
  const [resolved, setResolved] = useState<ResolvedState>();

  useEffect(() => {
    if (!src || !needsAuthenticatedStorageFetch(src)) {
      return;
    }

    const controller = new AbortController();
    let createdUrl: string | undefined;

    void (async () => {
      try {
        const blob = await fetchStorageBlob(src, controller.signal);
        if (controller.signal.aborted) {
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setResolved({ kind: "ready", src, url: createdUrl });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }
        console.error("Failed to resolve storage object URL", error);
        setResolved({ kind: "error", src });
      }
    })();

    return () => {
      controller.abort();
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [src]);

  if (!src) {
    return { status: "idle" };
  }
  if (!needsAuthenticatedStorageFetch(src)) {
    return { status: "ready", url: src };
  }
  if (resolved?.src === src) {
    if (resolved.kind === "ready") {
      return { status: "ready", url: resolved.url };
    }
    return { status: "error" };
  }
  return { status: "loading" };
}
